import { ListKeyboardDelegate, useSelectableList } from '@react-aria/selection'
import { useListState } from '@react-stately/list'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { ListBoxProps } from 'components/ListBox/Types'
import { RefObject, useCallback, useEffect } from 'react'
import { KnowledgeBaseSnippet } from 'types'

const SELECT_KEYS = ['Enter', 'Tab']

interface SuggestionsSection {
  name: string
  children: KnowledgeBaseSnippet[]
}

interface Props extends ListBoxProps<SuggestionsSection> {
  inputRef: RefObject<HTMLInputElement>
  dropdownRef: RefObject<HTMLDivElement>
}

export const SnippetsList = ({ items, inputRef, dropdownRef, ...props }: Props) => {
  const state = useListState({
    ...props,
    items,
    selectionMode: 'single',
  })

  const keyboardDelegate = new ListKeyboardDelegate(state.collection, state.disabledKeys, inputRef)

  const { listProps } = useSelectableList({
    selectionManager: state.selectionManager,
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    ref: dropdownRef,
    keyboardDelegate,
    shouldFocusWrap: true,
  })

  const handleKeyDown = useCallback(
    (e: Event) => {
      if (!(e instanceof KeyboardEvent)) {
        return
      }

      listProps.onKeyDown?.(e as unknown as React.KeyboardEvent<HTMLElement>)

      if (SELECT_KEYS.includes(e.key)) {
        e.stopPropagation()
        state.selectionManager.replaceSelection(state.selectionManager.focusedKey)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  )

  useEffect(() => {
    const keyboardOwner = inputRef?.current
    keyboardOwner?.addEventListener('keydown', handleKeyDown)

    return () => {
      keyboardOwner?.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleKeyDown])

  return <ListBoxBase aria-label="Snippets" state={state} />
}
